import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Users, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ROLES = [
  { value: 'ADMIN_ESTADUAL', label: 'Admin Estadual', color: 'bg-blue-100 text-blue-800' },
  { value: 'CASA_CIVIL', label: 'Casa Civil', color: 'bg-blue-100 text-blue-800' },
  { value: 'CGE', label: 'CGE', color: 'bg-green-100 text-green-800' },
  { value: 'SECRETARIA_EXECUTORA', label: 'Secretaria Executora', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'UNIDADE_INTERNA', label: 'Unidade Interna', color: 'bg-orange-100 text-orange-800' },
  { value: 'JURIDICO', label: 'Jurídico', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'GOVERNADOR', label: 'Governador', color: 'bg-indigo-100 text-blue-800' },
  { value: 'OBSERVADOR_EXTERNO', label: 'Observador Externo', color: 'bg-slate-100 text-slate-800' },
];

export const UsersPage = () => {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/users');
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Erro ao carregar usuários');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [api]);

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;

    setUpdating(true);
    try {
      await api.put(`/users/${selectedUser.user_id}/role?role=${newRole}`);
      setUsers(users.map(u => 
        u.user_id === selectedUser.user_id ? { ...u, role: newRole } : u
      ));
      toast.success('Perfil atualizado com sucesso');
      setShowRoleDialog(false);
      setSelectedUser(null);
      setNewRole('');
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Erro ao atualizar perfil');
    } finally {
      setUpdating(false);
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowRoleDialog(true);
  };

  const getRoleBadge = (role) => {
    const r = ROLES.find(r => r.value === role);
    return <Badge className={r?.color || ''}>{r?.label || role}</Badge>;
  };

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="users-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Gestão de Usuários</h1>
            <p className="text-slate-500 mt-1">
              Gerencie os usuários e permissões do sistema
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1d4ed8]/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#1d4ed8]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{users.length}</p>
                  <p className="text-sm text-slate-500">Total de Usuários</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {users.filter(u => u.is_active).length}
                  </p>
                  <p className="text-sm text-slate-500">Usuários Ativos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {users.filter(u => u.role === 'ADMIN_ESTADUAL').length}
                  </p>
                  <p className="text-sm text-slate-500">Administradores</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>Usuários</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : users.length === 0 ? (
              <div className="empty-state py-16">
                <Users className="empty-state-icon" />
                <h3 className="empty-state-title">Nenhum usuário encontrado</h3>
                <p className="empty-state-description">
                  Não há usuários cadastrados neste tenant
                </p>
              </div>
            ) : (
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuário</TableHead>
                    <TableHead>E-mail</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead>Departamento</TableHead>
                    <TableHead>Cadastro</TableHead>
                    <TableHead className="w-24">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-9 h-9">
                            <AvatarImage src={user.picture} />
                            <AvatarFallback className="bg-[#1d4ed8] text-white text-sm">
                              {user.name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{user.name}</p>
                            <p className="text-xs font-mono text-slate-400">{user.user_id}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{user.email}</TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                      <TableCell className="text-slate-600">{user.department || '-'}</TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {user.created_at ? format(new Date(user.created_at), 'dd/MM/yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        {user.user_id !== currentUser?.user_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openRoleDialog(user)}
                            data-testid={`edit-role-${user.user_id}`}
                          >
                            Editar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Role Dialog */}
        <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Alterar Perfil</DialogTitle>
              <DialogDescription>
                Altere o perfil de acesso de {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue placeholder="Selecione o perfil" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRoleDialog(false)}>
                Cancelar
              </Button>
              <Button
                className="bg-[#1d4ed8] hover:bg-[#1e40af]"
                onClick={handleUpdateRole}
                disabled={updating || newRole === selectedUser?.role}
                data-testid="save-role-btn"
              >
                {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default UsersPage;
